/**
 * In-app confirmation dialog, a replacement for window.confirm.
 *
 * window.confirm only offers OK/Cancel and renders as "localhost says…", which
 * is useless when the question has more than two answers (save and notify,
 * save silently, cancel). This renders a centred card over the app with a
 * title, message lines, and a column of action buttons in the order given.
 *
 * Props:
 *   visible   - boolean
 *   title     - string
 *   lines     - array of strings; an empty string renders as a spacer
 *   actions   - [{ label, onPress, variant: "primary" | "secondary" | "danger" | "ghost" }]
 *   footnote  - optional small print under the buttons
 *   onCancel  - called on backdrop press / Escape / hardware back
 */

import React, { useEffect, useState, useRef, useCallback } from "react";
import { View, Text, StyleSheet, TouchableOpacity, Modal, Pressable } from "react-native";
import { colors, radius, shadows } from "../theme";

const ConfirmDialog = ({ visible, title, lines = [], actions = [], footnote = "", onCancel }) => {
  // Escape closes, like the native dialog did.
  useEffect(() => {
    if (!visible || typeof window === "undefined" || !onCancel) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible, onCancel]);

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onCancel}>
      <Pressable style={styles.overlay} onPress={onCancel}>
        {/* Stop presses inside the card from reaching the backdrop. */}
        <Pressable style={styles.card} onPress={() => {}}>
          {!!title && <Text style={styles.title}>{title}</Text>}
          <View style={styles.body}>
            {lines.map((line, i) =>
              line === "" ? (
                <View key={i} style={styles.spacer} />
              ) : (
                <Text key={i} style={styles.line}>
                  {line}
                </Text>
              )
            )}
          </View>
          <View style={styles.actions}>
            {actions.map((action) => {
              const variant = action.variant || "secondary";
              return (
                <TouchableOpacity
                  key={action.label}
                  style={[styles.button, styles[`${variant}Button`]]}
                  onPress={action.onPress}
                  activeOpacity={0.8}
                >
                  <Text style={[styles.buttonText, styles[`${variant}Text`]]}>{action.label}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
          {!!footnote && <Text style={styles.footnote}>{footnote}</Text>}
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: colors.overlay,
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  card: {
    width: "100%",
    maxWidth: 440,
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    padding: 20,
    ...shadows.raised,
  },
  title: { fontSize: 17, fontWeight: "700", color: colors.ink900, marginBottom: 12 },
  body: { marginBottom: 16 },
  line: { fontSize: 14, lineHeight: 20, color: colors.ink700 },
  spacer: { height: 8 },
  actions: { gap: 8 },
  button: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: radius.md,
    alignItems: "center",
    borderWidth: 1,
    borderColor: "transparent",
  },
  buttonText: { fontSize: 15, fontWeight: "600" },
  primaryButton: { backgroundColor: colors.primary },
  primaryText: { color: "#FFFFFF" },
  secondaryButton: { backgroundColor: colors.surfaceMuted, borderColor: colors.border },
  secondaryText: { color: colors.ink900 },
  dangerButton: { backgroundColor: colors.danger },
  dangerText: { color: "#FFFFFF" },
  ghostButton: { backgroundColor: "transparent" },
  ghostText: { color: colors.ink500 },
  footnote: { fontSize: 12, lineHeight: 16, color: colors.ink500, marginTop: 12 },
});

/**
 * Promise-based access to one ConfirmDialog, so async flows can read like the
 * window.confirm / alert calls they replace:
 *
 *   const { dialog, ask, confirm, notice } = useConfirmDialog();
 *   if (!(await confirm("Discard changes?", ["The original booking reloads."]))) return;
 *   await notice("Saved", ["The booking was updated."]);
 *   ...
 *   return <View>{...}{dialog}</View>;
 *
 * ask(opts)   resolves with the pressed action's `value`, or opts.cancelValue
 *             (default null) on backdrop / Escape.
 * confirm()   resolves true/false.
 * notice()    one OK button; resolves once dismissed.
 * Opening a new dialog while one is up settles the old one as cancelled.
 */
export function useConfirmDialog() {
  const [state, setState] = useState(null);
  const resolverRef = useRef(null);

  const settle = useCallback((value) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setState(null);
    if (resolve) resolve(value);
  }, []);

  const ask = useCallback(
    (opts) =>
      new Promise((resolve) => {
        if (resolverRef.current) resolverRef.current(opts.cancelValue ?? null);
        resolverRef.current = resolve;
        setState(opts);
      }),
    []
  );

  const confirm = useCallback(
    (title, lines = [], { okLabel = "OK", cancelLabel = "Cancel", danger = false, footnote = "" } = {}) =>
      ask({
        title,
        lines,
        footnote,
        cancelValue: false,
        actions: [
          { label: okLabel, value: true, variant: danger ? "danger" : "primary" },
          { label: cancelLabel, value: false, variant: "ghost" },
        ],
      }),
    [ask]
  );

  const notice = useCallback(
    (title, lines = [], { okLabel = "OK", danger = false } = {}) =>
      ask({
        title,
        lines,
        cancelValue: true,
        actions: [{ label: okLabel, value: true, variant: danger ? "danger" : "primary" }],
      }),
    [ask]
  );

  const onCancel = useCallback(() => settle(state?.cancelValue ?? null), [settle, state]);

  const dialog = state ? (
    <ConfirmDialog
      visible
      title={state.title}
      lines={state.lines || []}
      footnote={state.footnote || ""}
      actions={(state.actions || []).map((a) => ({
        label: a.label,
        variant: a.variant,
        onPress: () => settle(a.value),
      }))}
      onCancel={onCancel}
    />
  ) : null;

  return { dialog, ask, confirm, notice };
}

export default ConfirmDialog;
